import React, {FC, useEffect, useState} from 'react';
import {Route, Routes, useNavigate, useParams} from "react-router-dom";
import {connect, DispatchProp} from "react-redux";
import {RootState} from "../../../../reducers";
import FetchData from "../../../components/Fetch/FetchData";
import classNames from "classnames";
import Hero from "../../HomePage/components/Hero/Hero";
import AgeRestrictedContext from "../../../services/Common/AgeRestrictedContext";
import MenuComponent from "../components/MenuComponent";
import MenuAddItemPage from "./Item/MenuAddItemPage";
import CartButtonComponent from "../../../components/Cart/CartButtonComponent";
import CartStickyComponent from "../../../components/Cart/CartStickyComponent";
import AgeRestrictedModalComponent from "../components/AgeRestricted/AgeRestrictedModalComponent";
import MenuAdvertComponent from "../components/MenuAdvertComponent";
import {ReactComponent as MenuSVG} from "../../../../images/icons/menu.svg";
import {getStoreConfig, getStoreDefaultConfig} from "../../../services/Store/reducer";
import AppConfig from "../../../services/AppConfig";
import {usePrefix} from "../../../services/Prefix";
import StoreUtils from "../../../services/Store/StoreUtils";
import CartStickyContextPage from "../../Contexts/CartStickyContextPage";
import IframeWidget from "../../../utils/IframeWidget";
import {ReactComponent as ArrowTopSVG} from "../../../../images/icons/arrow-top.svg";
import {useTranslation} from "react-i18next";
import MenuPromotionsComponent from "../components/Promotion/MenuPromotionsComponent";
import {getPromotions} from "../../../services/Menu/reducer";
import MenuLocationFilterComponent from "../components/Filter/MenuLocationFilterComponent";
import {getSession} from "../../../services/Session/reducer";
import {getCart} from "../../../services/Cart/reducer";
import AgeRestrictedComponent from "../components/AgeRestricted/AgeRestrictedComponent";


type Props = ReturnType<typeof mapStateToProps> & DispatchProp;

const MenuPage: FC<Props> = (props) => {
    const [showAgeRestricted, setShowAgeRestricted] = useState(!AgeRestrictedContext.isAccepted() && AgeRestrictedContext.isAlcohol());
    const {t} = useTranslation();
    const {menuId} = useParams();
    const navigate = useNavigate();
    const prefix = usePrefix();
    const isDifferentMenuBaseOnType = false;
    const [isScrollToTopButtonVisible, setScrollToTopButtonVisible] = useState(false);

    const handleScroll = (scrollY: number) => {
        setScrollToTopButtonVisible(scrollY > 0);
    };

    useEffect(() => {
        if(AppConfig.isWidget()) {
            window.addEventListener('message', (event) => {
                const { type } = event.data;
                if (type === 'PARENT_SCROLL') {
                    handleScroll(event.data.iframeParentOffsetTop ?? 0);
                }
            });
        }
        else {
            window.addEventListener('scroll', () => handleScroll(window.scrollY));
            return () => window.removeEventListener('scroll', () => handleScroll(window.scrollY));
        }
    }, []);

    const goToCart = () => {
        navigate(AppConfig.getLinkWithPrefix('/cart', prefix));
    }

    const scrollToTop = () => {
        if (AppConfig.isWidget()) {
            IframeWidget.scrollTo(0);
        } else {
            window.scrollTo({top: 0, left: 0, behavior: "smooth"});
        }
    }

    return (
        <FetchData menus order>
            <CartStickyContextPage>
                <AgeRestrictedComponent>
                    <div className="menu-page">
                        {
                            isScrollToTopButtonVisible && (
                                <div className={"navigate-bottom"}>
                                    <button className={"btn btn-default btn-icon btn-lg"} onClick={scrollToTop}
                                            aria-label={t("modules.menu.action.scroll_to_top.title")}
                                    >
                                        <ArrowTopSVG aria-hidden="true" />
                                    </button>
                                </div>
                            )
                        }

                        <CartStickyComponent goToCart={goToCart} showVouchers/>

                        <div className="d-flex flex-column w-100 my-4 gap-2">
                            {props.session.order_type === "DELIVERY" && (
                                <MenuLocationFilterComponent/>
                            )}
                        </div>

                        {(!AppConfig.isNativeMobile()) && (
                            <>
                                {!StoreUtils.isMultistore(props.defaultConfig) && (
                                    <Hero config={props.config}/>
                                )}
                            </>
                        )}
                        {(!AppConfig.isNativeMobile()) && (
                            <>
                                <MenuAdvertComponent/>
                            </>
                        )}
                        <div className={"container-fluid"}>
                            <MenuPromotionsComponent promotions={props.promotions}/>
                            <h2>
                                <MenuSVG/> {t("modules.menu.field.menu.title")}
                            </h2>
                            {isDifferentMenuBaseOnType && (
                                <p>
                                    {t("modules.menu.field.different_menu.title")}
                                </p>
                            )}
                        </div>
                        {showAgeRestricted ? (
                            <>
                                <div>
                                    <AgeRestrictedModalComponent show={true} onSave={() => {
                                        setShowAgeRestricted(false)
                                    }}/>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={classNames('menu body-container body-container-cart')}>
                                    <MenuComponent menuId={menuId}/>
                                </div>
                                {
                                    props.cart.items.length > 0 && (
                                        <div className="btn-submit-bottom fixed-bottom btn-submit-container">
                                            <CartButtonComponent goToCart={goToCart}/>
                                        </div>
                                    )
                                }
                                <Routes>
                                    <Route path={`menu/:menuId/add/:id`} element={<MenuAddItemPage/>}/>
                                    <Route path={`:menuId/add/:id`} element={<MenuAddItemPage/>}/>
                                    <Route path={`add/:id`} element={<MenuAddItemPage/>}/>
                                </Routes>
                            </>
                        )}
                    </div>
                </AgeRestrictedComponent>
            </CartStickyContextPage>
        </FetchData>
    )
};

const mapStateToProps = (state: RootState) => ({
    config: getStoreConfig(state.config),
    defaultConfig: getStoreDefaultConfig(state.config),
    promotions: getPromotions(state.menu),
    session: getSession(state.session),
    cart: getCart(state.cart),
})

const connector = connect(mapStateToProps);
export default connector(MenuPage);
